import { graphql } from 'react-relay';
import React, { Fragment, useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link/link';
import { token } from '@atlaskit/tokens';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	FallbackComponentLegacyContentQuery as FallbackComponentLegacyContentQuery$data,
	FallbackComponentLegacyContentQueryVariables as FallbackComponentLegacyContentQuery$variables,
} from './__types__/FallbackComponentLegacyContentQuery.ts';
import { LegacyContentModal } from './LegacyContentModal.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.span({
	display: 'flex',
	padding: `${token('space.150')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> span:first-of-type': {
		paddingRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentSkeleton = styled.div({
	margin: '0 auto',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonContainer = styled.div({
	display: 'flex',
	padding: `${token('space.150')} 0 ${token('space.200')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		paddingRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentSkeletonLine = styled.div<CommentSkeletonLineProps>({
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: '12px',
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${(props: CommentSkeletonLineProps) => props.width}%`,
});

type FallbackComponentProps = {
	contentId?: string;
};

type CommentSkeletonLineProps = {
	width: number;
};

export const FallbackComponent = ({ contentId }: FallbackComponentProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { data, loading } = useQueryInterop<
		FallbackComponentLegacyContentQuery$data,
		FallbackComponentLegacyContentQuery$variables
	>(
		graphql`
			query FallbackComponentLegacyContentQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						body {
							view {
								value
								webresource {
									keys
								}
							}
						}
						history {
							createdBy {
								__typename
								... on KnownUser {
									accountId
								}
								profilePicture {
									path
								}
								displayName
							}
						}
						type
					}
				}
			}
		`,
		{
			variables: { contentId: contentId ?? '' },
		},
	);
	const buildUserObject = (data: any) => {
		const userId = data?.content?.nodes?.[0]?.history?.createdBy?.accountId;
		const avatarUrl = data?.content?.nodes?.[0]?.history?.createdBy?.profilePicture?.path;
		const displayName = data?.content?.nodes?.[0]?.history?.createdBy?.displayName;
		return {
			userId,
			avatarUrl,
			displayName,
		};
	};
	const handleCloseModal = useCallback(() => {
		setIsOpen(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);
	const user = buildUserObject(data);
	const legacyContent = data?.content?.nodes?.[0]?.body?.view?.value ?? '';
	const webResourceKeys = data?.content?.nodes?.[0]?.body?.view?.webresource?.keys ?? [];
	if (loading) {
		return (
			<SkeletonContainer>
				<ShortcutIcon spacing="spacious" label="legacy-modal-shortcut" />
				<CommentSkeleton>
					<CommentSkeletonLine width={100} />
					<CommentSkeletonLine width={100} />
					<CommentSkeletonLine width={75} />
				</CommentSkeleton>
			</SkeletonContainer>
		);
	}
	return (
		<Fragment>
			<StyledContainer>
				<ShortcutIcon spacing="spacious" label="legacy-modal-shortcut" />
				<span>
					{/* oxlint-disable-next-line @atlassian/a11y/anchor-is-valid */}
					<Link href="#" onClick={handleCloseModal}>
						<FormattedMessage
							id="comment-extension-handlers.fallback.action"
							defaultMessage="Click to view content"
							description="clickable link to display more content"
						/>
					</Link>{' '}
					<FormattedMessage
						id="comment-extension-handlers.fallback.reason"
						defaultMessage="that can’t display inline as it was created in a legacy version of Confluence."
						description="reason for why we cannot display the fallback content"
					/>
				</span>
			</StyledContainer>
			<ModalTransition>
				{isOpen && (
					<LegacyContentModal
						onClose={setIsOpen}
						content={legacyContent}
						/* @ts-ignore LegacyContentModal possibly has incorrect types. Should be string[] and not [] */
						webResources={webResourceKeys}
						user={user}
					/>
				)}
			</ModalTransition>
		</Fragment>
	);
};
